import { z } from 'zod';
import { dotSchema } from './dot.js';
import { snapshotSchema } from './snapshot.js';

/**
 * Realtime events broadcast by the backend to every client connected to a
 * given hill chart room.
 *
 * Design choice - every event that touches a dot ships the full `Dot`
 * (see `dot:added` and `dot:updated` below). Rationale:
 *  - Frontend replaces the entry in its cache verbatim - no field-by-field
 *    reconciliation logic to maintain.
 *  - Adding a new column on `dots` (e.g. `priority`) won't silently desync
 *    connected clients: they pick up the new field on the next event.
 *  - Payloads are tiny (a dot is < 300 bytes JSON), so the gain in client
 *    simplicity is worth the few extra bytes on the wire.
 *
 * `dot:removed` is the only exception: an id is all we need to evict a
 * row locally, and shipping a "tombstone dot" would be misleading.
 *
 * Discriminated on `type` so the frontend can narrow with a simple switch.
 * New event types MUST be additive to preserve forward-compat with older
 * connected clients (they'll just ignore unknown types).
 */
export const wsEventSchema = z.discriminatedUnion('type', [
  /**
   * A dot was just mutated through the HTTP API (PATCH dot) or resynced
   * from the upstream provider (upstream rename / URL change). The `dot`
   * field reflects the row as it now stands - clients overwrite their local
   * copy with it.
   */
  z.object({
    type: z.literal('dot:updated'),
    hillChartId: z.string().uuid(),
    dot: dotSchema,
  }),
  /**
   * A new scope was imported - either on initial hill creation (never
   * broadcast; no one is connected yet) or by a resync that found a scope
   * present upstream but missing locally.
   */
  z.object({
    type: z.literal('dot:added'),
    hillChartId: z.string().uuid(),
    dot: dotSchema,
  }),
  /**
   * A resync found that a scope no longer exists upstream. Clients should
   * drop the row locally.
   */
  z.object({
    type: z.literal('dot:removed'),
    hillChartId: z.string().uuid(),
    dotId: z.string().uuid(),
  }),
  /**
   * A snapshot was captured - manually via POST /snapshots or by the
   * daily scheduler. Frontend panels subscribing to the hill should
   * prepend this row in their list.
   */
  z.object({
    type: z.literal('snapshot:created'),
    hillChartId: z.string().uuid(),
    snapshot: snapshotSchema,
  }),
  /**
   * The hill was marked as Done by a user. Emitted as the last event a
   * connected client will receive on this room: the server then closes the
   * socket with code 4423 (HILL_COMPLETED) so the client cannot keep trying
   * to push live updates. The frontend uses the payload to patch its hill
   * cache and redirect to the read-only archive view.
   */
  z.object({
    type: z.literal('hill:completed'),
    hillChartId: z.string().uuid(),
    completedAt: z.string().datetime(),
    completedByUserId: z.string().uuid().nullable(),
  }),
  /**
   * The upstream project backing this hill was deleted. The hill itself is
   * preserved as Active+Orphaned - the user must manually mark it Done or
   * delete it. The frontend surfaces a warning banner in response to this
   * event.
   *
   * Emitted by `lib/resync.ts`: when GitLab answers 404 for the hill's
   * project, the resync stamps `external_project_deleted_at` and broadcasts
   * this event. The stamping UPDATE is guarded on the column still being
   * null, so only the resync that first notices broadcasts - later ones find
   * the marker already set and stay silent rather than re-announcing the
   * banner on every hourly tick.
   *
   * All three resync triggers can therefore produce it: opening an active
   * GitLab hill, the manual Sync button, and the hourly job.
   */
  z.object({
    type: z.literal('hill:orphaned'),
    hillChartId: z.string().uuid(),
    externalProjectDeletedAt: z.string().datetime(),
  }),
  /**
   * A user renamed the hill. `title` is user-owned: resync never touches it,
   * so this event is the only way the title changes after import. It lets
   * connected clients update the header without a refetch.
   *
   * Additive member: older clients that do not know this type simply ignore
   * it, which is why it carries no field they would otherwise need.
   */
  z.object({
    type: z.literal('hill:renamed'),
    hillChartId: z.string().uuid(),
    title: z.string(),
  }),
  /**
   * The hill was hard-deleted (active or done). Connected clients receive
   * this event then a close frame with code 4410 (HILL_DELETED). Frontend
   * evicts the hill + snapshot caches and bounces the user back to the list.
   */
  z.object({
    type: z.literal('hill:deleted'),
    hillChartId: z.string().uuid(),
  }),
]);
export type WsEvent = z.infer<typeof wsEventSchema>;
